import { Text } from "@imminently/react/ui/core/text";
import Radio from "@mui/material/Radio";
import { styled } from "@mui/material/styles";
import get from "lodash/get";
import {
  ObservationNeuroLimb,
  Observation,
  limbNeuroColorLabels,
  limbNeuroWarmthLabels,
  limbNeuroMovementLabels,
  limbNeuroSensationLabels,
  limbNeuroCapillaryRefillLabels,
  limbNeuroPulseLabels
} from "@/resources/types";
import React from "react";
import { UseFormWatch } from "react-hook-form";


type LimbNeuro = NonNullable<NonNullable<Observation["neuro"]>["limbNeuro"]>;

//# region TableSement

type TableSegmentProps = {
  heading: string;
  options: Array<{ label: string; value: string; }>;
  values: {
    [key in keyof LimbNeuro]: string;
  };
  setValue: (key: keyof TableSegmentProps["values"], value: string) => unknown;
}

const segmentValueKeys: Array<keyof TableSegmentProps["values"]> = [
  "leftArm",
  "rightArm",
  "leftLeg",
  "rightLeg",
];

const getValueForMetric = (
  limbNeuro: LimbNeuro,
  key: keyof TableSegmentProps["values"],
  metric: keyof ObservationNeuroLimb,
) => get(limbNeuro, [key, metric]);

const getValuesForMetric = (
  limbNeuro: LimbNeuro,
  metric: keyof ObservationNeuroLimb,
): TableSegmentProps["values"] => {
  const acc: TableSegmentProps["values"] = { leftArm: "", leftLeg: "", rightArm: "", rightLeg: "" };

  return segmentValueKeys.reduce(
    (a, key) => {
      a[key] = getValueForMetric(limbNeuro, key, metric);

      return a;
    },
    acc
  );
};


const TableSegment: React.FC<TableSegmentProps> = p => {
  const {
    heading,
    options,
    values,
    setValue,
  } = p;

  return (
    <>
      <tr className='segmentHeading'>
        <td colSpan={5}>
          <Text bold>{heading}</Text>
        </td>
      </tr>

      {
        options.map(it => {
          const checkboxesJSX = segmentValueKeys.map(key => (
            <td key={key}>
              <Radio
                checked={values[key] === it.value}
                onChange={(_, checked) => checked && setValue(key, it.value)}
              />
            </td>
          ));

          return (
            <tr key={it.value}>
              <td><Text>{it.label}</Text></td>

              {checkboxesJSX}
            </tr>
          );
        })
      }
    </>
  );
};

//# endregion

const Table = styled("table")`
  width: 100%;
  overflow: auto;
  table-layout: fixed;
  border-collapse: collapse;

  th, td {
    &:nth-of-type(1) {
      width: 30%;
    }
  }

  thead tr:nth-of-type(2) th {
    padding-top: 1rem;
  }

  td {
    padding-top: 1rem;

    &:nth-of-type(n + 2) { text-align: center; }
  }

  tr:not(.segmentHeading) td:nth-of-type(1) {
    padding-left: 2rem;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 1;

    &, & th {
      background-color: #fff;
    }
  }
`;

const colourOptions: Array<{ label: string; value: NonNullable<ObservationNeuroLimb["colour"]> }> = [
  { label: limbNeuroColorLabels.normal, value: "normal" },
  { label: limbNeuroColorLabels.mottled, value: "mottled" },
  { label: limbNeuroColorLabels.dusky, value: "dusky" },
  { label: limbNeuroColorLabels.pale, value: "pale" },
  { label: limbNeuroColorLabels.flushed, value: "flushed" },
];
const warmthOptions: Array<{ label: string; value: NonNullable<ObservationNeuroLimb["warmth"]> }> = [
  { label: limbNeuroWarmthLabels.hot, value: "hot" },
  { label: limbNeuroWarmthLabels.warm, value: "warm" },
  { label: limbNeuroWarmthLabels.cool, value: "cool" },
  { label: limbNeuroWarmthLabels.cold, value: "cold" },
];
const movementOptions: Array<{ label: string; value: NonNullable<ObservationNeuroLimb["movement"]> }> = [
  { label: limbNeuroMovementLabels.normal, value: "normal" },
  { label: limbNeuroMovementLabels.impaired, value: "impaired" },
  { label: limbNeuroMovementLabels.absent, value: "absent" },
];
const sensationOptions: Array<{ label: string; value: NonNullable<ObservationNeuroLimb["sensation"]> }> = [
  { label: limbNeuroSensationLabels.normal, value: "normal" },
  { label: limbNeuroSensationLabels.impaired, value: "impaired" },
  { label: limbNeuroSensationLabels.absent, value: "absent" },
];
const capillaryRefillOptions: Array<{ label: string; value: NonNullable<ObservationNeuroLimb["capillaryRefill"]> }> = [
  { label: limbNeuroCapillaryRefillLabels.normal, value: "normal" },
  { label: limbNeuroCapillaryRefillLabels.absent, value: "absent" },
];
const pulseOptions: Array<{ label: string; value: NonNullable<ObservationNeuroLimb["pulse"]> }> = [
  { label: limbNeuroPulseLabels.normal, value: "normal" },
  { label: limbNeuroPulseLabels.weak, value: "weak" },
  { label: limbNeuroPulseLabels.doppler, value: "doppler" },
  { label: limbNeuroPulseLabels.absent, value: "absent" },
];

export type LimbNeurovascularProps = {
  watch: UseFormWatch<Pick<Observation, "neuro">>
  setValue: (path: string, value: string) => unknown;
}
export const LimbNeurovascular: React.FC<LimbNeurovascularProps> = p => {
  const { watch, setValue } = p;

  const limbNeuro = watch("neuro.limbNeuro") || {};

  return (
    <Table>
      <thead>
        <tr>
          <th />
          <th colSpan={2}>
            <Text bold>Arms</Text>
          </th>
          <th colSpan={2}>
            <Text bold>Legs</Text>
          </th>
        </tr>
        <tr>
          <th />
          <th>
            <Text bold>L</Text>
          </th>
          <th>
            <Text bold>R</Text>
          </th>
          <th>
            <Text bold>L</Text>
          </th>
          <th>
            <Text bold>R</Text>
          </th>
        </tr>
      </thead>

      <tbody>
        <TableSegment
          heading="Colour"
          options={colourOptions}
          values={getValuesForMetric(limbNeuro, "colour")}
          setValue={(key, value) => {
            const nextOptionWithValue = colourOptions.find(it => it.value === value);
            if (nextOptionWithValue === undefined) return;

            setValue(`neuro.limbNeuro.${key}.colour`, nextOptionWithValue.value);
          }}
        />

        <TableSegment
          heading="Warmth"
          options={warmthOptions}
          values={getValuesForMetric(limbNeuro, "warmth")}
          setValue={(key, value) => {
            const nextOptionWithValue = warmthOptions.find(it => it.value === value);
            if (nextOptionWithValue === undefined) return;

            setValue(`neuro.limbNeuro.${key}.warmth`, nextOptionWithValue.value);
          }}
        />

        <TableSegment
          heading="Movement"
          options={movementOptions}
          values={getValuesForMetric(limbNeuro, "movement")}
          setValue={(key, value) => {
            const nextOptionWithValue = movementOptions.find(it => it.value === value);
            if (nextOptionWithValue === undefined) return;

            setValue(`neuro.limbNeuro.${key}.movement`, nextOptionWithValue.value);
          }}
        />

        <TableSegment
          heading="Sensation"
          options={sensationOptions}
          values={getValuesForMetric(limbNeuro, "sensation")}
          setValue={(key, value) => {
            const nextOptionWithValue = sensationOptions.find(it => it.value === value);
            if (nextOptionWithValue === undefined) return;

            setValue(`neuro.limbNeuro.${key}.sensation`, nextOptionWithValue.value);
          }}
        />

        <TableSegment
          heading="Capillary refill"
          options={capillaryRefillOptions}
          values={getValuesForMetric(limbNeuro, "capillaryRefill")}
          setValue={(key, value) => {
            const nextOptionWithValue = capillaryRefillOptions.find(it => it.value === value);
            if (nextOptionWithValue === undefined) return;

            setValue(`neuro.limbNeuro.${key}.capillaryRefill`, nextOptionWithValue.value);
          }}
        />

        <TableSegment
          heading="Pulse"
          options={pulseOptions}
          values={getValuesForMetric(limbNeuro, "pulse")}
          setValue={(key, value) => {
            const nextOptionWithValue = pulseOptions.find(it => it.value === value);
            if (nextOptionWithValue === undefined) return;

            setValue(`neuro.limbNeuro.${key}.pulse`, nextOptionWithValue.value);
          }}
        />
      </tbody>
    </Table>
  );
};
